import localforage from 'localforage'
import VueJwtDecode from 'vue-jwt-decode'
import FingerprintJS from '@fingerprintjs/fingerprintjs'

export const auth = {
    state: {
        status: 'loggedOut',
        token: null,
        oneSignalId: ''
    },
    mutations: {
        SET_AUTH_STATUS(state, status) {
            state.status = status
        },
        SET_AUTH_TOKEN(state, token) {
            state.token = token
        },
        SET_AUTH_ONE_SIGNAL_ID(state, oneSignalId) {
            state.oneSignalId = oneSignalId
        }
    },
    actions: {
        removeAccount({ commit }) {
            commit('SET_AUTH_STATUS', 'pending')
            return new Promise((resolve, reject) => {
                this.$api.post('api/users/soft-delete-user').then(res => {
                    commit('SET_AUTH_STATUS', 'loggedOut')
                    resolve(res)
                }).catch((err) => {
                    commit('SET_AUTH_STATUS', 'loggedIn')
                    reject(err)
                })
            })
        },
        register({ commit }, payload) {
            return new Promise((resolve, reject) => {
                commit('SET_AUTH_STATUS', 'pending')
                this.$api.post('api/users/register', payload).then(response => {
                    resolve(response)
                }).catch(error => {
                    reject(error)
                }).finally(() => {
                    commit('SET_AUTH_STATUS', 'loggedOut')
                })
            })
        },
        async authLogin({ commit, dispatch }, payload) {
            try {
                commit('SET_AUTH_STATUS', 'pending')

                await dispatch('fetchDeviceInfo')

                const response = await this.$api.post('api/users/token.json', {
                    email: payload.email,
                    password_hash: payload.password
                })

                commit('SET_AUTH_TOKEN', response.data.data.token)
                commit('SET_AUTH_STATUS', 'loggedIn')

                await localforage.setItem('authToken', response.data.data.token)

                dispatch('sendExternalUserId')
                if (response.data.data.first_login) {
                    await dispatch('subscriptions/fetchActiveSubscription')
                    dispatch('modalShow', 'firstLogin')
                }
            } catch (err) {
                commit('SET_AUTH_STATUS', 'loggedOut')
                throw err
            }
        },
        sendExternalUserId({ state, commit }) {
            const userId = VueJwtDecode.decode(state.token).sub
            const OneSignal = window.OneSignal
            OneSignal.push(() => {
                OneSignal.getUserId().then(isSubscribed => {
                    if (isSubscribed) {
                        commit('SET_AUTH_ONE_SIGNAL_ID', isSubscribed)
                        OneSignal.push(() => {
                            OneSignal.setExternalUserId(userId)
                        })
                    }
                })
            })
            // send user id to native app
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.toggleMessageHandler) {
                window.webkit.messageHandlers.toggleMessageHandler.postMessage({
                    externalUserId: userId.toString()
                })
            }
        },
        authLogout({ commit }) {
            return new Promise((resolve, reject) => {
                // stop all requests
                window.stop()
                // remove session on server
                localforage.removeItem('authToken').then(() => {
                    // remove from store
                    commit('SET_AUTH_STATUS', 'loggedOut')
                    localforage.getItem('initialState').then(initialState => {
                        // replace state with initial state after e.g logout
                        this.replaceState(initialState)
                        resolve()
                    }).catch(e => {
                        console.error(e)
                        resolve()
                    })
                }).catch(error => {
                    console.error(error)
                    reject(error)
                })
            })
        },
        authLoadToken({ commit }) {
            return localforage.getItem('authToken').then(token => {
                commit('SET_AUTH_TOKEN', token)
            })
        },
        authCheckTokenValidity({ commit, dispatch, getters }) {
            return new Promise((resolve, reject) => {
                dispatch('authLoadToken').then(() => {
                    // check if there is auth data in storage
                    if (getters.authToken !== null && getters.authToken !== undefined) {
                        // check if user has agreed to terms and conditions
                        dispatch('termsAndConditions/checkIfAccepted')
                        commit('SET_AUTH_STATUS', 'loggedIn')
                        resolve()
                    } else {
                        reject(new Error('Błąd sesji logowania.'))
                    }
                })
            })
        },
        authResetPasswordStep1({ commit }, login) {
            return new Promise((resolve, reject) => {
                commit('SET_AUTH_STATUS', 'pending')
                this.$api.post('api/reset-password.json', {
                    email: login
                }, {
                    'Content-Type': 'multipart/form-data'
                }).then(response => {
                    if (response.status === 200) {
                        resolve()
                        commit('SET_AUTH_STATUS', 'waitingForEmail')
                    } else {
                        reject(response)
                    }
                }).catch(error => {
                    commit('SET_AUTH_STATUS', 'loggedOut')
                    reject(error)
                })
            })
        },
        authResetPasswordStep2({ commit }, payload) {
            return new Promise((resolve, reject) => {
                commit('SET_AUTH_STATUS', 'pending')
                return this.$api.post('api/new-password.json', {
                    guid: payload.guid,
                    password: payload.password,
                    password_repeat: payload.password_repeat
                }, {
                    'Content-Type': 'multipart/form-data'
                }).then(response => {
                    if (response.status === 200) {
                        commit('SET_AUTH_STATUS', 'loggedOut')
                        resolve()
                    } else {
                        reject(response)
                    }
                }).catch(error => {
                    commit('SET_AUTH_STATUS', 'loggedOut')
                    reject(error)
                })
            })
        },
        async fetchDeviceInfo() {
            try {
            // Initialize the agent at application startup.
                const fpPromise = FingerprintJS.load()
                // Get the visitor identifier when you need it.
                const fp = await fpPromise
                const result = await fp.get()
                await localforage.setItem('deviceInfo', result.visitorId)
                return result.visitorId
            } catch(error) {
                return error
            }
        }
    },
    getters: {
        authStatus: (state) => {
            return state.status
        },
        authToken: (state) => {
            return state.token
        },
        authUser: (state) => {
            if (state.token) {
                return VueJwtDecode.decode(state.token)
            }
        },
        authUserId: (state) => {
            if (state.token) {
                return VueJwtDecode.decode(state.token).sub
            }
        },
        authIsAdvisor: (state) => {
            let isAdvisor = false
            if (state.token) {
                const user = VueJwtDecode.decode(state.token)
                if (user.roles && user.roles.length > 0) {
                    isAdvisor = user.roles.some(role => role.symbol === 'admin' || role.symbol === 'advisor')
                }
            }
            return isAdvisor
        },
        authUserRole: (state) => {
            let role = null
            if (state.token) {
                const user = VueJwtDecode.decode(state.token)
                if (user.roles && user.roles.length > 0) {
                    role = user.roles
                }
            }
            return role
        },
        authOneSignalId: (state) => {
            return state.oneSignalId
        }
    }
}
